import React, { memo } from 'react';

interface SearchPaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export const SearchPagination: React.FC<SearchPaginationProps> = memo(({
    currentPage,
    totalPages,
    onPageChange
}) => {
    // Generate pagination numbers
    const getPaginationNumbers = () => {
        const delta = 2; // Number of pages to show on each side of current page
        const range = [];
        const rangeWithDots = [];
        
        // Calculate start and end of visible page numbers
        const start = Math.max(1, currentPage - delta);
        const end = Math.min(totalPages, currentPage + delta);
        
        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        
        // Add first page and dots if necessary
        if (start > 1) {
            rangeWithDots.push(1);
            if (start > 2) {
                rangeWithDots.push('...');
            }
        }
        
        // Add the main range
        rangeWithDots.push(...range);
        
        // Add dots and last page if necessary
        if (end < totalPages) {
            if (end < totalPages - 1) {
                rangeWithDots.push('...');
            }
            rangeWithDots.push(totalPages);
        }
        
        return rangeWithDots;
    };

    if (totalPages <= 1) {
        return null;
    }

    return (
        <nav className="search-pagination" aria-label="Search results pagination">
            <ul className="search-pagination-list">
                {/* Previous Button */}
                <li className="search-pagination-item">
                    <button
                        className={`search-pagination-button previous un-button ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Go to previous page"
                    >
                        <svg className="svg-icon">
                            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-arrow-down" />
                        </svg>
                        <span>Previous</span>
                    </button>
                </li>
                
                {/* Page Numbers */}
                {getPaginationNumbers().map((pageNum, index) => (
                    <li key={index} className="search-pagination-item">
                        {pageNum === '...' ? (
                            <span className="search-pagination-ellipsis">...</span>
                        ) : (
                            <button
                                className={`search-pagination-button btn-secondary ${pageNum === currentPage ? 'active' : ''}`}
                                onClick={() => onPageChange(pageNum as number)}
                                aria-label={`Go to page ${pageNum}`}
                                aria-current={pageNum === currentPage ? 'page' : undefined}
                            >
                                {pageNum}
                            </button>
                        )}
                    </li>
                ))} 
                
                {/* Next Button */}
                <li className="search-pagination-item">
                    <button
                        className={`search-pagination-button next un-button ${currentPage === totalPages ? 'disabled' : ''}`}
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Go to next page"
                    >
                        <span>Next</span>
                        <svg className="svg-icon">
                            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-arrow-down" />
                        </svg>
                    </button>
                </li>            
            </ul>        
        </nav>
    );
});

SearchPagination.displayName = 'SearchPagination';
